import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Dumbbell, Edit, Eye, Copy, Plus, UserCheck, ChevronDown, ChevronUp, Trash2, Upload, Download, Crown, Globe, ImageOff } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { getDifficultyLabel } from '../../utils/constants';

const SECTIONS = [
  { key: 'beginner', color: 'text-green-400', border: 'border-green-500/30', bg: 'bg-green-500/10' },
  { key: 'intermediate', color: 'text-yellow-400', border: 'border-yellow-500/30', bg: 'bg-yellow-500/10' },
  { key: 'advanced', color: 'text-red-400', border: 'border-red-500/30', bg: 'bg-red-500/10' },
];

export const AdminWorkoutsTab = ({ workouts, onDuplicate, onDelete, users, onBulkImport, onExport }) => {
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState({});
  
  const getAssigneeLabel = (assigned) => {
    const ids = Array.isArray(assigned) ? assigned : assigned ? [assigned] : [];
    if (!ids.length) return null;
    const first = users?.find(u => u.id === ids[0])?.name?.split(' ')[0] || 'Unknown';
    return ids.length > 1 ? `${first} +${ids.length - 1}` : first;
  };

  const allWorkouts = Array.isArray(workouts) ? workouts : [];
  
  const grouped = {
    beginner: allWorkouts.filter(w => w.difficulty === 'beginner'),
    intermediate: allWorkouts.filter(w => w.difficulty === 'intermediate'),
    advanced: allWorkouts.filter(w => w.difficulty === 'advanced'),
  };

  const toggle = (key) => setCollapsed(prev => ({ ...prev, [key]: !prev[key] }));

  const renderCard = (workout) => (
    <div key={workout.id} className="bg-card border border-border p-4" data-testid={`admin-workout-card-${workout.id}`}>
      {/* Cover photo thumbnail — gives the admin a quick visual of which workouts
          have a hero image set on the public card and which still need one. */}
      <div className="relative mb-3 -mx-4 -mt-4">
        {workout.image_url ? (
          <img
            src={workout.image_url}
            alt=""
            className="w-full h-32 object-cover"
            data-testid={`admin-workout-thumb-${workout.id}`}
          />
        ) : (
          <div
            className="w-full h-32 bg-black/40 border-b border-dashed border-border flex items-center justify-center text-muted-foreground text-xs"
            data-testid={`admin-workout-no-thumb-${workout.id}`}
          >
            <div className="text-center">
              <ImageOff className="h-5 w-5 mx-auto mb-1 opacity-50" />
              No cover photo
            </div>
          </div>
        )}
        {/* Premium / Free badge — top-left so it's instantly scannable */}
        <span
          className={`absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-heading uppercase tracking-wider border ${
            workout.premium_only
              ? 'bg-primary/20 text-primary border-primary/60'
              : 'bg-green-500/20 text-green-300 border-green-500/40'
          }`}
          data-testid={`admin-workout-access-${workout.id}`}
        >
          {workout.premium_only ? (
            <><Crown className="h-3 w-3" /> Premium</>
          ) : (
            <><Globe className="h-3 w-3" /> Free</>
          )}
        </span>
      </div>

      <div className="flex items-start justify-between mb-3">
        <div>
          <h4 className="font-heading text-lg text-white uppercase tracking-wider">{workout.title}</h4>
          <p className="text-muted-foreground text-sm">{workout.duration_minutes} min · {getDifficultyLabel(workout.difficulty)}</p>
        </div>
        <Badge className="bg-primary/20 text-primary">{workout.category}</Badge>
      </div>
      <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
        <span><Dumbbell className="inline h-4 w-4 mr-1" />{workout.exercises?.length || 0} exercises</span>
        {workout.video_url && <span className="text-green-400">Has Video</span>}
        {getAssigneeLabel(workout.assigned_to) && (
          <span className="inline-flex items-center gap-1 text-blue-400" data-testid={`workout-assignees-${workout.id}`}>
            <UserCheck className="h-3 w-3" />
            {getAssigneeLabel(workout.assigned_to)}
          </span>
        )}
      </div>
      <div className="flex gap-2">
        <Button 
          size="sm" 
          className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90"
          onClick={() => navigate(`/admin/workout/${workout.id}`)}
          data-testid={`edit-workout-${workout.id}`}
        >
          <Edit className="h-4 w-4 mr-1" />
          Edit
        </Button>
        <Button 
          size="sm" 
          variant="outline" 
          className="border-white/20 text-white hover:bg-white/10"
          onClick={() => navigate(`/workout/${workout.id}`)}
          title="View"
        >
          <Eye className="h-4 w-4" />
        </Button>
        <Button 
          size="sm" 
          variant="outline" 
          className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10"
          onClick={() => onDuplicate(workout.id)}
          title="Duplicate"
          data-testid={`duplicate-workout-${workout.id}`}
        >
          <Copy className="h-4 w-4" />
        </Button>
        <Button 
          size="sm" 
          variant="outline" 
          className="border-red-500/30 text-red-400 hover:bg-red-500/10"
          onClick={() => onDelete(workout.id, workout.title)}
          title="Delete"
          data-testid={`delete-workout-${workout.id}`}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <div className="flex justify-end gap-2 mb-4">
        {onBulkImport && (
          <Button
            onClick={onBulkImport}
            variant="outline"
            className="border-border text-white hover:bg-white/5 font-heading tracking-wider uppercase"
            data-testid="bulk-import-workouts-btn"
          >
            <Upload className="h-4 w-4 mr-2" />
            Import Workouts
          </Button>
        )}
        {onExport && (
          <Button
            onClick={onExport}
            variant="outline"
            className="border-border text-white hover:bg-white/5 font-heading tracking-wider uppercase"
            data-testid="export-workouts-btn"
          >
            <Download className="h-4 w-4 mr-2" />
            Export Workouts
          </Button>
        )}
        <Button 
          onClick={() => navigate('/admin/workout/new')}
          className="bg-primary text-primary-foreground font-heading tracking-wider uppercase hover:bg-primary/90"
          data-testid="create-workout-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Create Workout
        </Button>
      </div>

      {/* Quick access-tier summary so the admin can see at a glance how many
          workouts gate behind premium vs are available as free previews. */}
      {allWorkouts.length > 0 && (
        <div className="grid grid-cols-3 gap-3 mb-5" data-testid="workouts-access-summary">
          <div className="bg-card border border-border p-3 text-center">
            <div className="text-2xl font-heading text-white">{allWorkouts.length}</div>
            <div className="text-xs text-muted-foreground uppercase tracking-wider mt-1">Total Workouts</div>
          </div>
          <div className="bg-primary/10 border border-primary/40 p-3 text-center" data-testid="premium-count-card">
            <div className="text-2xl font-heading text-primary flex items-center justify-center gap-2">
              <Crown className="h-5 w-5" />
              {allWorkouts.filter(w => w.premium_only).length}
            </div>
            <div className="text-xs text-primary/90 uppercase tracking-wider mt-1">Premium Locked</div>
          </div>
          <div className="bg-green-500/10 border border-green-500/40 p-3 text-center" data-testid="free-count-card">
            <div className="text-2xl font-heading text-green-300 flex items-center justify-center gap-2">
              <Globe className="h-5 w-5" />
              {allWorkouts.filter(w => !w.premium_only).length}
            </div>
            <div className="text-xs text-green-300 uppercase tracking-wider mt-1">Free Preview</div>
          </div>
        </div>
      )}

      {SECTIONS.map(({ key, color, border, bg }) => {
        const items = grouped[key] || [];
        const isCollapsed = collapsed[key];
        return (
          <div key={key} className="mb-6">
            <button
              onClick={() => toggle(key)}
              className={`w-full flex items-center justify-between p-3 mb-3 border ${border} ${bg} rounded transition-colors hover:opacity-90`}
              data-testid={`section-${key}`}
            >
              <div className="flex items-center gap-3">
                <span className={`font-heading text-xl uppercase tracking-wider ${color}`}>
                  {getDifficultyLabel(key)}
                </span>
                <Badge variant="outline" className={`${border} ${color}`}>
                  {items.length} workouts
                </Badge>
              </div>
              {isCollapsed ? <ChevronDown className={`h-5 w-5 ${color}`} /> : <ChevronUp className={`h-5 w-5 ${color}`} />}
            </button>
            {!isCollapsed && (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {items.map(renderCard)}
              </div>
            )}
          </div>
        );
      })}
    </motion.div>
  );
};
